import { useState } from "react";
import { toast } from "sonner";
import { Share, X, Check, Copy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BigButton } from "./MobileShell";

const digits = (s) => String(s || "").replace(/\D/g, "").slice(-10);
const input = "w-full bg-white border border-brand-border rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-brand-ink";

async function findOrCreateLead(f, location) {
  const probe = f.email.trim() || f.name.trim().split(" ")[0];
  const { data } = await api.get("/crm/leads", { params: { search: probe, location: "all" } });
  const hit = data.find((l) => (f.email.trim() && (l.email || "").toLowerCase() === f.email.trim().toLowerCase())
    || (digits(f.phone).length >= 7 && digits(l.phone) === digits(f.phone)));
  if (hit) return hit;
  const [first, ...rest] = f.name.trim().split(" ");
  const { data: created } = await api.post("/crm/leads", { first_name: first, last_name: rest.join(" "), company: f.company.trim() || null,
    email: f.email.trim() || null, phone: f.phone.trim() || null, location, stage: "New", source: "Quick proposal (Hub app)" });
  return created;
}

export function QuickProposalSheet({ offices: initial, lead, onClose, onSent }) {
  const { user } = useAuth();
  const [offices] = useState(initial);
  const [f, setF] = useState(() => ({ name: lead ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim() : "", company: lead?.company || "", email: lead?.email || "", phone: lead?.phone || "" }));
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const locations = [...new Set(offices.map((o) => o.location))];
  const create = async () => {
    if (!f.name.trim()) { toast.error("Add the client's name"); return; }
    if (!lead && !f.email.trim() && !f.phone.trim()) { toast.error("Add an email or phone so it can be logged in the CRM"); return; }
    setBusy(true);
    try {
      const who = f.company.trim() || f.name.trim();
      const valid = new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10);
      const { data: prop } = await api.post("/proposals", { title: `${who} — ${locations.join(" & ")}`, client_name: f.name.trim(),
        account_exec: user?.name || user?.email || "", valid_until: valid, locations, office_ids: offices.map((o) => o.id) });
      const url = `${window.location.origin}/p/${prop.share_token}`;
      try {
        const target = lead || await findOrCreateLead(f, locations[0]);
        await api.post(`/crm/hubspot/leads/${target.id}/attach-proposal`, { proposal_id: prop.id, origin: window.location.origin });
      } catch (e) { toast.error("Proposal created, but it couldn't be logged in the CRM", { description: e.userMessage }); }
      await api.post(`/proposals/${prop.id}/publish`, { published: true });
      setLink(url); onSent?.();
    } catch (e) { toast.error("Couldn't create the proposal", { description: e.userMessage }); }
    finally { setBusy(false); }
  };
  const text = () => `Hi ${f.name.trim().split(" ")[0]}, here's your Bond Collective proposal for ${offices.length === 1 ? `office ${offices[0].office_number}` : `${offices.length} offices`} at ${locations.join(" & ")}:`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Bond Collective proposal", text: text(), url: link }); } catch { /* dismissed */ }
    } else { await navigator.clipboard?.writeText(`${text()} ${link}`); toast.success("Message and link copied"); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose} data-testid="qp-sheet">
      <div className="w-full max-w-lg bg-brand-page rounded-t-3xl sm:rounded-3xl p-5 space-y-4 animate-in slide-in-from-bottom-4" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center">
          <h2 className="flex-1 font-serif text-2xl">{link ? "Proposal ready" : "Send a proposal"}</h2>
          <button onClick={onClose} className="p-2 -mr-2" aria-label="Close" data-testid="qp-close"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-brand-muted" data-testid="qp-offices">{offices.map((o) => `${/^\d/.test(o.office_number) ? "Office " : ""}${o.office_number} · $${Math.round(o.monthly_price || 0).toLocaleString()}/mo`).join("  ·  ")}</p>
        {link ? (
          <div className="space-y-3" data-testid="qp-done">
            <p className="text-sm text-brand-ink flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> Published and logged in the CRM as Proposal Sent.</p>
            <BigButton onClick={share} className="bg-brand-primary text-brand-page inline-flex items-center justify-center gap-2" data-testid="qp-share"><Share className="h-5 w-5" /> Share with {f.name.trim().split(" ")[0]}</BigButton>
            <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} className="w-full inline-flex items-center justify-center gap-2 text-sm text-brand-muted py-2" data-testid="qp-copy"><Copy className="h-4 w-4" /> Copy link only</button>
          </div>
        ) : (
          <div className="space-y-3">
            <input autoFocus placeholder="Client name *" value={f.name} onChange={set("name")} className={input} autoComplete="name" data-testid="qp-name" />
            <input placeholder="Company" value={f.company} onChange={set("company")} className={input} autoComplete="organization" data-testid="qp-company" />
            <div className="grid grid-cols-2 gap-2">
              <input type="email" placeholder="Email" value={f.email} onChange={set("email")} className={input} autoComplete="email" data-testid="qp-email" />
              <input type="tel" placeholder="Phone" value={f.phone} onChange={set("phone")} className={input} autoComplete="tel" data-testid="qp-phone" />
            </div>
            <BigButton onClick={create} disabled={busy} className="bg-brand-primary text-brand-page" data-testid="qp-create">{busy ? "Creating…" : "Create proposal"}</BigButton>
          </div>
        )}
      </div>
    </div>
  );
}
